import { cn, isSymbolicaUser } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/hooks/useSettings';
import { trackActivityVisibilityChanged, trackLogout } from '@/analytics';
import {
  setPaperTheme,
  usePaperThemePreference,
  type ThemePreference,
} from '@/hooks/useThemeFlag';
import { PaperSettingsShell } from './PaperSettingsShell';
import { SettingsSection } from './SettingsSection';
import { PAPER_CONTROL_RADIUS_CLASS } from './paperShapes';

type Visibility = 'show_all' | 'results_only';

const BUTTONS: { id: Visibility; label: string }[] = [
  { id: 'show_all', label: 'All activity, including tool calls' },
  { id: 'results_only', label: 'Results only' },
];

const APPEARANCE_OPTIONS: { id: ThemePreference; label: string }[] = [
  { id: 'paper-auto', label: 'System' },
  { id: 'paper-light', label: 'Light' },
  { id: 'paper-dark', label: 'Dark' },
];

export const GeneralSettingsPaper = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { settings, setActivityVisibility } = useSettings();
  const canViewAllActivity = isSymbolicaUser(user?.email);
  const activityVisibility: Visibility = canViewAllActivity
    ? settings.activityVisibility
    : 'results_only';

  const initials = user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : '';
  const displayName = user ? `${user.firstName} ${user.lastName}` : 'Guest';
  const metaLine = user?.email ?? '';

  // Reflect the user's stored choice (including ``paper-auto``) so the
  // toggle shows what they picked, not what it currently resolves to.
  const appearance = usePaperThemePreference();

  return (
    <PaperSettingsShell
      activeTab="general"
      account={{
        initials,
        displayName,
        metaLine,
        onLogout: isAuthenticated
          ? () => { trackLogout(); logout(); }
          : undefined,
      }}
    >
      <SettingsSection
        eyebrow="Display"
        title="Appearance"
        subtitle="Light, dark, or follow your system."
      >
        <div
          className={cn(
            'inline-flex p-[3px] rounded-lg w-full',
            'bg-[var(--surface)] border border-solid border-[var(--hair)]',
          )}
        >
          {APPEARANCE_OPTIONS.map((opt) => {
            const isActive = opt.id === appearance;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setPaperTheme(opt.id)}
                className={cn(
                  'flex-1 h-[34px] text-[12px] cursor-pointer',
                  PAPER_CONTROL_RADIUS_CLASS,
                  'transition-colors duration-150',
                  isActive
                    ? 'bg-[var(--bg-alt)] text-[var(--fg)] font-medium'
                    : 'bg-transparent text-[var(--fg-3)] hover:text-[var(--fg)]',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </SettingsSection>

      {canViewAllActivity && (
        <SettingsSection
          eyebrow="Conversation"
          title="Activity Display"
          subtitle="Control what is shown in the conversation timeline."
        >
          <div
            className={cn(
              'inline-flex p-[3px] rounded-lg w-full',
              'bg-[var(--surface)] border border-solid border-[var(--hair)]',
            )}
          >
            {BUTTONS.map((b) => {
              const isActive = b.id === activityVisibility;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => {
                    setActivityVisibility(b.id);
                    trackActivityVisibilityChanged(b.id);
                  }}
                  className={cn(
                    'flex-1 h-[34px] text-[12px] cursor-pointer',
                    PAPER_CONTROL_RADIUS_CLASS,
                    'transition-colors duration-150',
                    isActive
                      ? 'bg-[var(--bg-alt)] text-[var(--fg)] font-medium'
                      : 'bg-transparent text-[var(--fg-3)] hover:text-[var(--fg)]',
                  )}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        </SettingsSection>
      )}
    </PaperSettingsShell>
  );
};
